import {runInAction} from 'mobx';
import {observer} from 'mobx-react-lite';
import React, {useCallback, useEffect, useState} from 'react';
import {EButton, EFlatButton, EForm} from 'webapp-lib/Components';

import {DialogContentActions} from '#/Dialog/styles';
import Loading from '#/Loading';
import {DefaultValueInResponse} from '@lexian/api/documents-api';
import {getFormattedAddress} from '_/sharedComponents/CreateFromTemplateWizard/fill-placeholders/AddressPlaceholderField';
import {Placeholder, PlaceholderPage} from '_/store/template/template.types';
import {flatten} from '_/store/template/template.utils';
import {useForm} from '_/utilities/form';

import {DocumentTitle} from '../CreateFromTemplateWizard.styles';
import {WizardStepProps} from '../CreateFromTemplateWizard.types';

import {EntityPlaceholderField} from './EntityPlaceholderFields';
import {
  FormColumn,
  StyledFieldset,
  StyledFillPlaceholders,
} from './FillPlaceholders.styles';
import {PlaceholderField} from './PlaceholderFields';

type Props = {
  casusId?: string;
  fileName?: string;
  isLoading: boolean;
  pagedPlaceholder: PlaceholderPage;
  onParentEntityChange: (params: {
    pageKey: string;
    parentPlaceholderKey: string;
    parentEntityId: string;
    placeholderKey: string;
  }) => Promise<DefaultValueInResponse[] | void>;
} & WizardStepProps;

const UFillPlaceholders = ({
  casusId,
  fileName,
  isLoading,
  pagedPlaceholder,
  backLabel,
  nextLabel,
  onNextClick,
  onBackClick,
  onParentEntityChange,
}: Props): React.ReactElement => {
  const [changedEntity, setChangedEntity] = useState<{
    parentEntityId: string;
    placeholder: Placeholder;
  }>();

  const saveValues = () => {
    const values = form.getValues();
    // `runInAction` prevents console warning thrown by MobX due to
    // changing observable values without using on action
    runInAction(() =>
      pagedPlaceholder.placeholders
        .flatMap((p) => [p, ...flatten(p.placeholders ?? [])])
        .filter((p) => p.type !== 'GROUP')
        .forEach((placeholder) => {
          const value = values[placeholder.key];
          placeholder.value = value === '' ? null : value;
        })
    );
  };

  const form = useForm({
    onSubmit: () => {
      saveValues();
      onNextClick();
    },
  });

  useEffect(() => {
    if (!changedEntity) {
      return;
    }
    const {parentEntityId, placeholder} = changedEntity;
    placeholder.placeholders
      ?.flatMap((p) =>
        p.type === 'GROUP' ? (p.placeholders as Placeholder[]) : [p]
      )
      .forEach(async (p) => {
        const values = parentEntityId
          ? await onParentEntityChange({
              pageKey: pagedPlaceholder.key,
              parentPlaceholderKey: placeholder.key,
              placeholderKey: p.key,
              parentEntityId,
            })
          : [
              {
                ...p.defaultValue,
                options: [],
                entityId: undefined,
                value: '',
              } as DefaultValueInResponse,
            ];

        runInAction(() => {
          if (values !== undefined) {
            const valuesInResponse = values as DefaultValueInResponse[];
            p.defaultValue = valuesInResponse.map(
              (v): DefaultValueInResponse => ({
                options: v.options,
                entityId: v.entityId,
                value: v.value,
              })
            );
          } else {
            p.defaultValue = [
              {
                options: [],
                entityId: undefined,
                value: '',
              },
            ];
          }

          if (!p.doNotAutoUpdate) {
            form.setValue(p.key, getValue(p, placeholder));
          }
        });
      });
    setChangedEntity(undefined);
  }, [changedEntity, form, onParentEntityChange, pagedPlaceholder.key]);

  const getPlaceholderField = useCallback(
    (placeholder: Placeholder, autofocus: boolean): React.ReactElement => {
      if (
        placeholder.type === 'CONTACT' ||
        placeholder.type === 'INVOLVED_PARTY' ||
        placeholder.type === 'COLLABORATOR' ||
        placeholder.type === 'CASUS' ||
        placeholder.type === 'CLIENT' ||
        placeholder.type === 'USER' ||
        placeholder.type === 'OFFICE_LOCATION' ||
        placeholder.type === 'SPECIALITY_FIELD' ||
        placeholder.type === 'CLIENT_MONEY_BANK_ACCOUNT'
      ) {
        return (
          <React.Fragment key={placeholder.key}>
            <EntityPlaceholderField
              casusId={casusId}
              form={form}
              placeholder={placeholder}
              onParentEntityChange={(parentEntityId) =>
                setChangedEntity({parentEntityId, placeholder})
              }
            />
            {placeholder.placeholders?.map((p) =>
              getPlaceholderField(p, false)
            )}
          </React.Fragment>
        );
      }

      if (placeholder.type === 'GROUP') {
        return (
          <StyledFieldset key={placeholder.key}>
            <legend>{placeholder.label}</legend>
            {placeholder.placeholders?.map((p) =>
              getPlaceholderField(p, autofocus)
            )}
          </StyledFieldset>
        );
      }

      return (
        <PlaceholderField
          key={placeholder.key}
          autofocus={autofocus}
          form={form}
          placeholder={placeholder}
        />
      );
    },
    [casusId, form]
  );

  const goBack = () => {
    saveValues();
    onBackClick();
  };

  return (
    <StyledFillPlaceholders>
      {fileName ? <DocumentTitle>{fileName}</DocumentTitle> : null}
      <EForm onSubmit={form.handleSubmit}>
        {isLoading ? (
          <Loading />
        ) : (
          <FormColumn key={pagedPlaceholder.key}>
            {pagedPlaceholder.placeholders.map((placeholder, index) =>
              getPlaceholderField(placeholder, index === 0)
            )}
          </FormColumn>
        )}
        <DialogContentActions stepperActions={true}>
          <EFlatButton onClick={goBack}>{backLabel}</EFlatButton>
          <EButton>{nextLabel}</EButton>
        </DialogContentActions>
      </EForm>
    </StyledFillPlaceholders>
  );
};

export const FillPlaceholders = observer(UFillPlaceholders);

const getValue = (placeholder: Placeholder, parentPlaceholder: Placeholder) => {
  if (placeholder.type === 'ADDRESS' && !placeholder.editFieldType) {
    return placeholder.defaultValue[0].entityId;
  }
  if (placeholder.defaultValue[0]) {
    return getFormattedValue(placeholder.defaultValue[0].value, placeholder);
  }
  if (parentPlaceholder.defaultValue[0]?.options?.[0]) {
    return getFormattedValue(
      parentPlaceholder.defaultValue[0].options[0].fieldValue,
      parentPlaceholder
    );
  }
  return '';
};

const getFormattedValue = (value: string, placeholder: Placeholder) => {
  if (value && placeholder.type === 'ADDRESS') {
    return getFormattedAddress(value, placeholder.editFieldType);
  }
  return value;
};
